import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { queryKeys, reportsApi, campaignsApi } from '../../lib/api/resources';
import { count, money, percent, date } from '../../lib/format';
import {
  Card,
  EmptyState,
  ErrorState,
  MetricCard,
  Skeleton,
  Button,
  BudgetBar,
} from '../../components/ui';

/**
 * The operator's first screen: how the account is delivering right now.
 *
 * <p>Designed for a fresh install where every number is zero, not only for a busy account. A panel
 * that looks broken on day one is the first impression the product makes.
 */
export function DashboardPage() {
  const summary = useQuery({
    queryKey: queryKeys.summary(),
    queryFn: () => reportsApi.summary(),
  });

  const activeCampaigns = useQuery({
    queryKey: queryKeys.campaigns({ status: 'ACTIVE', size: 5 }),
    queryFn: () => campaignsApi.list({ status: 'ACTIVE', size: 5 }),
  });

  if (summary.isError) {
    return (
      <ErrorState
        message={(summary.error as Error).message}
        onRetry={() => void summary.refetch()}
      />
    );
  }

  const data = summary.data;
  const hasDelivery = (data?.impressions ?? 0) > 0;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-headline-lg text-text-primary">Panel</h1>
          {data && (
            <p className="text-body-md text-text-secondary">
              {date(data.from)} — {date(data.to)}
            </p>
          )}
        </div>
        <Link to="/campaigns/new">
          <Button variant="primary">Nueva campaña</Button>
        </Link>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summary.isLoading ? (
          Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-28" />)
        ) : (
          <>
            <MetricCard label="Impresiones" value={count(data!.impressions)} />
            <MetricCard label="Clics" value={count(data!.clicks)} />
            <MetricCard label="CTR" value={percent(data!.ctr)} />
            <MetricCard
              label="Consumido"
              value={money(data!.revenue)}
              hint="Acumulado histórico"
            />
          </>
        )}
      </div>

      <Card title="Entrega diaria">
        {summary.isLoading ? (
          <Skeleton className="h-64" />
        ) : hasDelivery ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data!.daily}>
              <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={12} tickMargin={8} />
              <YAxis stroke="#94A3B8" fontSize={12} />
              <Tooltip
                contentStyle={{
                  background: '#1E293B',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  color: '#F8FAFC',
                }}
              />
              <Line
                type="monotone"
                dataKey="impressions"
                name="Impresiones"
                stroke="#0066FF"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="clicks"
                name="Clics"
                stroke="#10B981"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            title="Todavía no hay entrega"
            description="Cuando una campaña activa empiece a servir anuncios, la evolución diaria aparecerá aquí."
            action={
              <Link to="/campaigns/new">
                <Button variant="primary">Crear la primera campaña</Button>
              </Link>
            }
          />
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Campañas activas">
          {activeCampaigns.isLoading ? (
            <Skeleton className="h-32" />
          ) : activeCampaigns.data && activeCampaigns.data.content.length > 0 ? (
            <ul className="flex flex-col gap-3">
              {activeCampaigns.data.content.map((campaign) => (
                <li key={campaign.id} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-3">
                    <Link
                      to={`/campaigns/${campaign.id}`}
                      className="truncate text-body-md text-text-primary hover:text-electric-blue"
                    >
                      {campaign.name}
                    </Link>
                    <span className="shrink-0 font-mono text-body-md text-text-secondary">
                      {money(campaign.budgetConsumed)} / {money(campaign.budgetTotal)}
                    </span>
                  </div>
                  <BudgetBar consumed={campaign.budgetConsumed} total={campaign.budgetTotal} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Ninguna campaña activa"
              description="Las campañas empiezan en borrador. Lanza una para que empiece a servir."
            />
          )}
        </Card>

        <Card title="Estado de la cuenta">
          {summary.isLoading ? (
            <Skeleton className="h-32" />
          ) : (
            <dl className="flex flex-col gap-3">
              <Row label="Campañas activas" value={count(data!.activeCampaigns)} />
              <Row label="Campañas totales" value={count(data!.totalCampaigns)} />
              <Row label="Anunciantes activos" value={count(data!.activeAdvertisers)} />
              <Row
                label="Eventos rechazados"
                value={count(data!.rejectedEvents)}
                hint="No facturados. Ver detalle en cada campaña."
              />
            </dl>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <div>
        <dt className="text-body-md text-text-secondary">{label}</dt>
        {hint && <p className="text-label-md text-text-secondary">{hint}</p>}
      </div>
      <dd className="font-mono text-body-lg text-text-primary">{value}</dd>
    </div>
  );
}
